import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment'
import axios from 'axios';
import { APIURL } from '../../config';
import CustomerCard from './CustomerCard/CustomerCard';
import DetailsPane from './DetailsPane'
import CustomerForm from 'src/components/forms/CustomerForm/CustomerForm';
import './Customers.css';
import makeStyles from "@mui/styles/makeStyles";
import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENTAPIKEY } from '../../config';
import { TextField, IconButton, Button, Autocomplete, MenuItem, Select, InputLabel, FormControl, Dialog, DialogActions, DialogContent, InputAdornment, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY })

export default function Customer(props) {
    const [state, setState] = useState({
        customers: [],
        active: [],
        inactive: [],
        prospect: [],
        dormant: [],
        activeList: 'active',
        searchTerm: '',
        customerSearchResults: [],
        isLoading: true,
        current_customer: null,
        edit: false,
        selectedMenu: '',
        openCustomerForm: false,
        newCustLoading: true,
        customerFormData: {},
        loadedScope: null,
        refreshing: false
    })
    const [loggedToSegment, setLoggedToSegment] = useState(false);
    const [inputValue, setInputValue] = useState(''); // Autocomplete inputValue

    // The backend gives Admins every company customer; this toggle lets them narrow to their own.
    // Admins open on All
    const isAdmin = props.user.type === 'Admin';
    const [customerScope, setCustomerScope] = useState(isAdmin ? 'all' : 'associated');
    const fetchSeq = useRef(0);
    const handledLocationKey = useRef(null);

    const location = useLocation()

    const logToSegment = () => {
        if (loggedToSegment) {
            return;
        }
        let page = String(window.location.href).split("/").pop()
        console.log(page, window.location.href)
        if (page.includes("fieldworker") || page === "" || page === "#") {
            page = "Home";
        } else {
            page = String(page).charAt(0).toUpperCase() + String(page).toLowerCase().slice(1);
        }
        console.log(page)
        analytics.page(page, {
            title: page,
            path: page,
        }, {
            userId: String(props.user.emp_id),
        })
        console.log("logged to segment successfully")
        setLoggedToSegment(true);
    }

    const getData = async () => {
        const seq = ++fetchSeq.current;
        const scope = isAdmin ? customerScope : 'associated';
        setState(prev => ({ ...prev, refreshing: true }));
        props.checkTokenExpiry();
        axios.defaults.headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            "Access-Control-Allow-Headers": 'Origin, X-Requested-With, Content-Type, Accept, Authorization',
            "Access-Control-Allow-Methods": "PUT, GET, POST, DELETE, OPTIONS",
            "ACCESS-CONTROL-ALLOW-CREDENTIAL": true,
            Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token')
        };

        const customerTypeScope = props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager";

        let cusData;
        try {
            cusData = await axios.get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.comp_id,
                    employee_id: props.user.emp_id,
                    type: customerTypeScope,
                    // Adds active_project_count to each customer for the list grid
                    include_project_counts: true,
                    ...(isAdmin && scope === 'associated' ? { scope: 'associated' } : {}),
                    ...(props.selectedProgramId ? { program_id: props.selectedProgramId } : {})
                }
            });
        } catch (err) {
            console.error(err);
            if (seq === fetchSeq.current) setState(prev => ({ ...prev, refreshing: false }));
            return;
        }

        // A newer request (e.g. the admin toggled scope again) has replaced this one
        if (seq !== fetchSeq.current) return;

        let cusList = cusData.data;

        let active = cusList.filter((cus) => cus.cus_status === 'Active');
        let inactive = cusList.filter((cus) => cus.cus_status === 'Inactive');
        let prospect = cusList.filter((cus) => cus.cus_status === "Prospect");
        let dormant = cusList.filter((cus) => !['Active', 'Inactive', 'Prospect'].includes(cus.cus_status));

        // No customer is preselected: the page opens on the list. A customer linked from
        // another page (location.customersProps) is opened by the effect below.
        setState(prev => ({
            ...prev,
            customers: cusList,
            active: active,
            inactive: inactive,
            prospect: prospect,
            dormant: dormant,
            customerSearchResults: { active, inactive, prospect, dormant }[prev.activeList] || active,
            isLoading: false,
            refreshing: false,
            loadedScope: scope
        }));
    };

    useEffect(() => {
        getData();
        logToSegment();
        let text = props?.user?.comp_settings.filter(setting => [101, 102, 104].includes(setting.feature_id)).length > 0 ? 'Clients'
            : props?.user?.comp_settings.filter(setting => setting.feature_id == 105).length > 0 ? 'Members' : 'Customers';
        setState(prev => ({
            ...prev,
            selectedMenu: text
        }));
    }, [props.selectedProgramId, customerScope]);

    // Runs once per navigation. A plain visit to /customers (including clicking it in the
    // sidebar while already here) shows the list; a link with customersProps opens that customer.
    useEffect(() => {
        if (state.isLoading || handledLocationKey.current === location.key) return;

        const nextCustomerId = location?.customersProps?.cus_id;
        if (!nextCustomerId) {
            handledLocationKey.current = location.key;
            setState((prev) => ({ ...prev, current_customer: null, edit: false }));
            setInputValue('');
            return;
        }

        const selectedCustomer = state.customers.find((customer) => customer.cus_id === nextCustomerId);
        // An Admin narrowed to their own customers; widen to all so the linked one can open
        if (!selectedCustomer && isAdmin && state.loadedScope !== 'all') {
            setCustomerScope('all');
            return;
        }
        handledLocationKey.current = location.key;
        const nextSearchTerm = selectedCustomer?.cus_name || state.searchTerm;
        const nextEdit = !!location?.customersProps?.edit;

        let nextActiveList = state.activeList;
        let nextCustomerSearchResults = state.customerSearchResults;

        if (selectedCustomer?.cus_status === 'Active') {
            nextActiveList = 'active';
            nextCustomerSearchResults = state.active;
        } else if (selectedCustomer?.cus_status === 'Inactive') {
            nextActiveList = 'inactive';
            nextCustomerSearchResults = state.inactive;
        } else if (selectedCustomer?.cus_status === 'Prospect') {
            nextActiveList = 'prospect';
            nextCustomerSearchResults = state.prospect;
        } else if (selectedCustomer?.cus_status === 'Dormant') {
            nextActiveList = 'dormant';
            nextCustomerSearchResults = state.dormant;
        }

        setState((prev) => ({
            ...prev,
            current_customer: nextCustomerId,
            edit: nextEdit,
            activeList: nextActiveList,
            customerSearchResults: nextCustomerSearchResults,
            searchTerm: nextSearchTerm,
        }));

        if (selectedCustomer?.cus_name) {
            setInputValue(selectedCustomer.cus_name);
        }
    }, [
        location.key,
        state.isLoading,
        state.loadedScope,
        state.customers,
        state.active,
        state.inactive,
        state.prospect,
        state.dormant,
    ]);

    const showCustomerDetails = (key) => {
        setState(prev => ({
            ...prev,
            current_customer: key
        }))
    }

    const backToList = () => {
        setState(prev => ({ ...prev, current_customer: null, edit: false }));
        setInputValue('');
    }

    const handleScopeChange = (event, value) => {
        if (value) setCustomerScope(value);
    }

    const showNewCustomer = async () => {
        setState(prev => ({
            ...prev,
            openCustomerForm: true
        }))
        await fetchNewCustData()
    }

    const showCustomerList = () => {
        if (state.customerSearchResults.length > 0) {
            return state.customerSearchResults.map(
                customers => {
                    return <CustomerCard
                        {...props}
                        key={customers.cus_id}
                        id={customers.cus_id}
                        customer={customers}
                        onClick={showCustomerDetails}
                        selected={state.current_customer === customers.cus_id ? 'active' : 'inactive'}
                    />
                }
            )
        }
    }

    const searchProjHandler = (e) => {
        var searchQuery = e.target.value
        setState(prev => ({
            ...prev,
            searchTerm: searchQuery
        }))
    }

    const searchProjResults = (e) => {
        e.preventDefault()
        var searchQuery = e.target.value
        var searchTerm = new RegExp(searchQuery, 'i')
        let customerSearchResults;

        const filterCustomer = (cusData) => {
            return cusData.filter((proj) => {
                if (searchTerm.exec(proj.cus_name) || searchTerm.exec(proj.DDDID) || searchTerm.exec(proj.cus_local_id)) {
                    return proj
                }
                else {
                    return null
                }
            }).filter((proj) => !!proj)
        }

        if (state.activeList === 'active') {
            customerSearchResults = filterCustomer(state.active)
        } else if (state.activeList === 'inactive') {
            customerSearchResults = filterCustomer(state.inactive)
        } else if (state.activeList === 'prospect') {
            customerSearchResults = filterCustomer(state.prospect)
        } else if (state.activeList === 'dormant') {
            customerSearchResults = filterCustomer(state.dormant)
        }
    };

    const toggleActive = (screen) => {
        if ((screen === 'active' && state.activeList === 'active') || (screen === 'inactive' && state.activeList === 'inactive') || (screen === 'prospect' && state.activeList === 'prospect') || (screen === 'dormant' && state.activeList === 'dormant')) {

            return
        } else {
            let cusData = [];
            if (screen === 'active') {
                ;
                cusData = state.active;
            } else if (screen === 'inactive') {
                cusData = state.inactive;
            } else if (screen === 'prospect') {
                cusData = state.prospect;
            } else if (screen === 'dormant') {
                cusData = state.dormant;
            }

            let filtered_customer = cusData;
            setState(prev => ({
                ...prev,
                searchTerm: '', activeList: screen, customerSearchResults: filtered_customer, current_customer: filtered_customer.length > 0 ? filtered_customer[0].cus_id : null
            }))

        }
    }

    const isNewFormValidated = (objForVal) => {
        console.log('>>>>>>>>>>> ', objForVal)
        var missingField = {
            isValidated: true
        }
        let temp = Object.keys(objForVal)
        if (temp[0] === 'selectedEmp') {

            let selectedEm = objForVal.selectedEmp
            for (let i = 0; i < selectedEm.length; i++) {
                for (let objKey in selectedEm[i]) {
                    if (
                        selectedEm[i][objKey] === "" ||
                        selectedEm[i][objKey] === undefined ||
                        selectedEm[i][objKey].length === 0
                    ) {
                        missingField.isValidated = false;
                        missingField.missingKey = objKey
                        return missingField
                    }
                }
            }
        }




        for (let objKey in objForVal) {
            if ((objKey !== "Cemail" && objKey !== "designation") &&
                (objForVal[objKey] === "" ||
                    objForVal[objKey] === undefined ||
                    objForVal[objKey]?.length === 0)
            ) {
                if (
                    objKey === "address2" ||
                    objKey === "location_addr2" ||
                    objKey === "tComment" ||
                    objKey === "cus_local_id" ||
                    //objKey === "ddd_id" ||
                    objKey === "address_line_2" ||
                    // objKey === "email" ||
                    objKey === "pSelfPay" ||
                    objKey === "pPrimaryPayer" ||
                    objKey === "pSecondaryPayer" ||
                    objKey === "medicaid_id" ||
                    objKey === "medicaid_eligible" ||
                    objKey === "sign_page_date" ||
                    objKey === "last_plan_approval_date" ||
                    objKey === "last_assess_date" ||
                    objKey === "renewal_date" ||
                    //objKey === "DDDID" ||
                    objKey === "personal_days_off" ||
                    objKey === "vacation_days" ||
                    objKey === "DDD_tier" ||
                    objKey === "diagnose_code" ||
                    objKey === "func_criteria_met" ||
                    objKey === "waiver_program" ||
                    objKey === "NJCATSelfcare" ||
                    objKey === "NJCATBehavioral" ||
                    objKey === "NJCATMedical" ||
                    objKey === "NJCATFCA" ||
                    objKey === "MedicaidEndDate" ||
                    objKey === "AgencyAssignDate" ||
                    objKey === "County" ||
                    objKey === "Race" ||
                    objKey === "Ethnicity" ||
                    objKey === "TransferOutReason" ||
                    objKey === "TransferOutDate" ||
                    objKey === "tContacts" ||
                    objKey === "place01char" ||
                    objKey === "place02char" ||
                    objKey === "place03char" ||
                    objKey === "place04date" ||
                    objKey === "place05date" ||
                    objKey === "place06number" ||
                    objKey === "place07number" ||
                    objKey === "HasPet" ||
                    objKey === "ProviderGenderPreference" ||
                    objKey === "LanguagePreference" ||
                    objKey === "PreferRepeatProviders" ||
                    objKey === "DriverLicenseNumber" ||
                    objKey === "DLExpiryDate" ||
                    objKey === "DLIssueDate" ||
                    objKey === "DLIssueState"
                ) {
                    // return true
                    continue;
                } else if (objForVal["cus_is_company"] === true && objForVal["company_name"] === "") {
                    missingField.isValidated = false;
                    missingField.missingKey = "company_name"
                    return missingField;
                }
                else {
                    console.log(objKey);

                    if (
                        (objKey === "company_name" &&
                            objForVal["first_name"] !== "" &&
                            objForVal["last_name"] !== "") ||
                        ((objKey === "first_name" || objKey === "last_name") &&
                            objForVal["company_name"] !== "" && objForVal["company_name"] !== undefined) || objKey === "address_maps"
                    ) {
                        continue;
                    } else {
                        missingField.isValidated = false;
                        missingField.missingKey = objKey
                        return missingField;
                    }
                }
            } else if (Array.isArray(objForVal[objKey])) {
                let arrValid = objForVal[objKey].every(function (objTesting) {
                    for (let objTestKey in objTesting) {
                        console.log(objTestKey);
                        if (
                            objTesting[objTestKey] === "" ||
                            objTesting[objTestKey] === undefined
                        ) {
                            if (
                                objTestKey === "address_line_2" ||
                                objTestKey === "location_addr2"
                            ) {
                                // return true
                                continue;
                            } else {
                                missingField.isValidated = false;
                                missingField.missingKey = objKey
                                return missingField;
                            }
                        }
                    }
                    return true;
                });
                if (!arrValid) return false;
            } else if (objKey === "active_plan_version") {
                for (const digit of objForVal[objKey]) {
                    if (Number.isNaN(parseInt(digit)) && digit !== ".") {
                        alert("PROJECT ACTIVE PLAN VERSION has to be a number");
                        objForVal[objKey] = "";
                        return false;
                    }
                }
            }
        }

        return missingField;
    };

    const handleCustomerSelection = (event, value) => {
        if (value && typeof value === 'object' && value?.cus_id) {
            console.log("Selected customer:", value);
            setState(prev => ({
                ...prev,
                current_customer: value.cus_id,
                searchTerm: value.cus_name
            }));
            setInputValue(value.cus_name);
        } else if (typeof value === 'string') {
            // Handle free text input - try to find matching customer
            console.log("Free text input:", value);
            const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const searchTerm = new RegExp(escaped, 'i');
            const matchingCustomer = state.customerSearchResults.find(cus => {
                const dobMatch = cus.dob ? cus.dob.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
                const dobFormatted  = dobMatch ? `${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}` : '';
                const dobNoLeadZero = dobMatch ? `${parseInt(dobMatch[2])}/${parseInt(dobMatch[3])}/${dobMatch[1]}` : '';
                const dobDigits     = dobMatch ? `${dobMatch[2]}${dobMatch[3]}${dobMatch[1]}` : '';
                return (
                    searchTerm.test(cus.cus_name) ||
                    searchTerm.test(cus.DDDID) ||
                    searchTerm.test(cus.cus_local_id) ||
                    searchTerm.test(cus.medicaid_id) ||
                    searchTerm.test(cus.dob) ||
                    searchTerm.test(dobFormatted) ||
                    searchTerm.test(dobNoLeadZero) ||
                    searchTerm.test(dobDigits)
                );
            });
            
            if (matchingCustomer) {
                setState(prev => ({
                    ...prev,
                    current_customer: matchingCustomer.cus_id,
                    searchTerm: matchingCustomer.cus_name
                }));
                setInputValue(matchingCustomer.cus_name);
            } else {
                // No match found, keep current customer if exists
                setInputValue(value);
                setState(prev => ({
                    ...prev,
                    searchTerm: value
                }));
            }
        } else {
            console.log("No customer selected or cleared");
            setState(prev => ({
                ...prev,
                searchTerm: ''
            }));
            setInputValue('');
        }
    }

    const handleCustomerTypeChange = (event) => {
        const selectedType = event.target.value;
        const newCustomerList = state[selectedType];

        // Changing the status filter shows the list for that status
        setState(prev => ({
            ...prev,
            activeList: selectedType,
            customerSearchResults: newCustomerList,
            current_customer: null,
            edit: false
        }));
        setInputValue('');
    };

    const formatDate = (value) => value ? moment.utc(value).format('MM/DD/YYYY') : '';

    // The company's field labels (set in MIS), loaded and resolved the same way as the client's Overview.
    const [schema, setSchema] = useState(null);
    const [schemaDefaults, setSchemaDefaults] = useState(null);

    useEffect(() => {
        const fetchSchema = async () => {
            try {
                const schemaRes = await axios.get(`${APIURL}/schema/table/customer`, { params: { companyID: props.comp_id } });
                const schemaDefaultsRes = await axios.get(`${APIURL}/schema/table-default/customer`, {
                    params: {
                        companyState: props.user.comp_settings?.find(setting => setting.feature_id === 99)?.state_id || null,
                        companyTypeOfBusiness: props.user.comp_settings?.find(setting => setting.feature_value === 'primaryBusinessType')?.feature_id || null,
                    }
                });
                setSchema(schemaRes.data);
                setSchemaDefaults(schemaDefaultsRes.data);
            } catch (err) {
                setSchema(null);
                setSchemaDefaults(null);
            }
        };
        fetchSchema();
    }, [props.comp_id]);

    const getFieldLabel = (field, fallback) => {
        const schemaField = schema?.find(s => s.columnName?.toLowerCase() === field.toLowerCase());
        if (schemaField && schemaField.columnLabel) return schemaField.columnLabel;
        const defaultField = schemaDefaults?.find(s => s.columnName?.toLowerCase() === field.toLowerCase());
        if (defaultField && defaultField.columnLabel) return defaultField.columnLabel;
        return fallback;
    };

    const customerColumns = [
        {
            field: 'cus_name', headerName: 'Name', flex: 1.5, minWidth: 180,
            // Styled like a link so it's clear the row opens the client
            renderCell: (params) => <span className="customer-name-link">{params.value}</span>
        },
        { field: 'dob', headerName: 'DOB', width: 120, valueFormatter: (value) => formatDate(value) },
        { field: 'DDDID', headerName: getFieldLabel('DDDID', 'DDD ID'), flex: 1, minWidth: 120 },
        {
            field: 'cus_address', headerName: 'Address', flex: 2, minWidth: 220,
            // Stored as "line1, line2, city, state, zip:..." — show the part before ':' without empty parts
            valueGetter: (value) => String(value || '').split(':')[0].split(',').map(part => part.trim()).filter(Boolean).join(', ')
        },
        { field: 'cus_phone', headerName: 'Phone', flex: 1, minWidth: 130 },
        { field: 'cus_status', headerName: 'Status', width: 110 },
        { field: 'cus_acquired_date', headerName: 'Acquired', width: 120, valueFormatter: (value) => formatDate(value) },
        { field: 'active_project_count', headerName: 'Active Projects', type: 'number', width: 130 },
        {
            field: 'open', headerName: '', width: 50, sortable: false, filterable: false,
            disableColumnMenu: true, disableExport: true, hideable: false, align: 'center',
            renderCell: () => <ChevronRightIcon color="action" />
        },
    ];

    const selectedCustomer = state.customers.find(customer => customer.cus_id === state.current_customer);

    const fetchNewCustData = async () => {
        let data = {
            employees: {
                list: (
                    await axios.get(APIURL + "/dropdowns/dropdownType/employeeslist", {
                        params: { comp_id: props.comp_id },
                    })
                ).data,
            },
            customers: {
                list: (
                    await axios.get(APIURL + "/dropdowns/dropdownType/customerslist", {
                        params: { comp_id: props.comp_id },
                    })
                ).data,
                customerType: (
                    await axios.get(APIURL + "/dropdowns/dropdownType/customerType", {
                        params: { comp_id: props.comp_id },
                    })
                ).data,
                customerStatus: (
                    await axios.get(APIURL + "/dropdowns/dropdownType/customerStatus", {
                        params: { comp_id: props.comp_id },
                    })
                ).data,
            },
        };

        setState(prev => ({
            ...prev,
            customerFormData: data,
            newCustLoading: false
        }))
    }

    // The page renders straight away; the grid's loading overlay covers the first load
    const listLoading = state.isLoading || state.refreshing;
    const dashBoard = (
            <div className="customersPage-mainDiv" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        {selectedCustomer && (
                            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={backToList}>
                                {state.selectedMenu}
                            </Button>
                        )}
                        <Autocomplete
                            freeSolo
                            clearOnBlur={true}
                            inputValue={inputValue}
                            onInputChange={(event, newInput) => setInputValue(newInput)}
                            options={state.customerSearchResults}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.cus_name}
                            filterOptions={(options, { inputValue }) => {
                                // Escape regex special chars in input
                                const escaped = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                                const searchTerm = new RegExp(escaped, 'i');
                                return options
                                    .map(proj => {
                                        // Generate multiple DOB formats for flexible searching
                                        const dobMatch = proj.dob ? proj.dob.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
                                        const dobFormatted   = dobMatch ? `${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}` : '';  // MM/DD/YYYY
                                        const dobNoLeadZero  = dobMatch ? `${parseInt(dobMatch[2])}/${parseInt(dobMatch[3])}/${dobMatch[1]}` : '';  // M/D/YYYY
                                        const dobDigits      = dobMatch ? `${dobMatch[2]}${dobMatch[3]}${dobMatch[1]}` : '';  // MMDDYYYY
                                        if (
                                            searchTerm.test(proj.cus_name) ||
                                            searchTerm.test(proj.DDDID) ||
                                            searchTerm.test(proj.cus_local_id) ||
                                            searchTerm.test(proj.medicaid_id) ||
                                            searchTerm.test(proj.dob) ||
                                            searchTerm.test(dobFormatted) ||
                                            searchTerm.test(dobNoLeadZero) ||
                                            searchTerm.test(dobDigits)
                                        ) {
                                            return proj;
                                        }
                                        return null;
                                    })
                                    .filter(Boolean);
                            }}
                             onChange={handleCustomerSelection}
                             disableClearable
                             renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={state.selectedMenu}
                                    variant="outlined"
                                    size="small"
                                    style={{ width: '300px' }} // Set consistent width
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, item) => (
                                <li {...props} key={item.key}>
                                    {item.cus_name}
                                </li>
                            )}
                        />
                        {/* Status and My/All only filter the list, so they're hidden on the details view */}
                        {!selectedCustomer && (
                            <FormControl variant="outlined" size="small" style={{ width: '200px' }}> {/* Adjusted width */}
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={state.activeList}
                                    onChange={handleCustomerTypeChange}
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                    <MenuItem value="prospect">Prospects</MenuItem>
                                    <MenuItem value="dormant">Dormant</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {!selectedCustomer && isAdmin && (
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                color="primary"
                                value={customerScope}
                                onChange={handleScopeChange}
                                aria-label={`Which ${state.selectedMenu} to show`}
                            >
                                <ToggleButton value="associated">My {state.selectedMenu}</ToggleButton>
                                <ToggleButton value="all">All {state.selectedMenu}</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {props.user.permissions.CreateNewCustomer && <Button onClick={showNewCustomer} variant="contained" color="primary">
                            New Customer
                        </Button>}
                    </div>
                </div>
                
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <div style={{ width: '100%' }}>
                        {selectedCustomer && (
                            <DetailsPane
                                status={state.viewOld ? 'Older' : 'Ongoing'}
                                comp_id={props.comp_id}
                                user={props.user}
                                history={props.history}
                                cus_id={state.current_customer}
                                cust_details={selectedCustomer}
                                project_details={state.customerSearchResults.find(customer => customer.cus_id === state.current_customer)}
                                new_customer={state.new_customer}
                                edit={state.edit}
                                selectedMenu={state.selectedMenu}
                                checkTokenExpiry={props.checkTokenExpiry}
                            />
                        )}
                        {!selectedCustomer && (listLoading || state.customerSearchResults.length !== 0) && (
                            <div style={{ height: '100%', padding: '0 10px 10px' }}>
                                <DataGridPro
                                    rows={state.customerSearchResults}
                                    columns={customerColumns}
                                    getRowId={(row) => row.cus_id}
                                    loading={listLoading}
                                    onRowClick={(params) => showCustomerDetails(params.id)}
                                    onCellKeyDown={(params, event) => {
                                        if (event.key === 'Enter') showCustomerDetails(params.id);
                                    }}
                                    disableRowSelectionOnClick
                                    showToolbar
                                    density="compact"
                                    sx={{
                                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                                        '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(63, 167, 214, 0.08)' },
                                        '& .customer-name-link': { color: 'primary.main', fontWeight: 600 },
                                        '& .MuiDataGrid-row:hover .customer-name-link': { textDecoration: 'underline' },
                                    }}
                                />
                            </div>
                        )}
                        {!selectedCustomer && !listLoading && state.customerSearchResults.length === 0 && (
                            <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '500px', marginTop: 10 }}>
                                <img
                                    src={state.customerSearchResults.length === 0 ? './nosearchresult.png' : './projectgraphic.png'}
                                    alt="projectgraphic"
                                    style={{ width: '15rem', height: '10rem', margin: 10 }}
                                />
                                <h5 style={{ fontFamily: 'Lato', marginTop: 48, marginBottom: 30 }} className="text-center">
                                    {state.customers.length !== 0
                                        ? 'No Customers Found'
                                        : isAdmin && customerScope === 'associated'
                                        ? `No ${state.selectedMenu} are associated with you. Switch to All ${state.selectedMenu} to see everyone.`
                                        : props.user.type === 'Fieldworker' || props.user.type === 'Support Coordinator'
                                        ? 'You are not assigned any project yet. Please contact your manager.'
                                        : 'You dont have any Customers yet'}
                                </h5>
                                <p style={{ fontFamily: 'Lato', fontSize: 12, width: '70%' }} className="text-muted text-center text-wrap">
                                    Customer's related information includes Overviews, Tasks, Employees, Artifacts, Comments, and Contacts will show here once you add customers.
                                    <br />
                                    On the top, you can search for customers or filter by type.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {
                    <Dialog 
                        fullWidth={true} maxWidth={'sm'} 
                        open={state.openCustomerForm}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                setState(prev => ({ ...prev, openCustomerForm: false }));
                            }
                        }}
                    >
                        <DialogActions>
                            <h4 style={{ marginLeft: '20px', marginTop: '10px' }}>New Customer</h4>
                            <hr style={{ marginBottom: '10px', marginTop: '10px', backgroundColor: '#e2ecfa' }}></hr>
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                sx={{ p: 0.25 }}
                                onClick={() => { setState(prev => ({ ...prev, openCustomerForm: false })); }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogActions>
                        <DialogContent sx={{ paddingTop: '0px' }}>
                            {state.newCustLoading &&
                                <div className='d-flex flex-row w-100 justify-content-center mt-5'>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            }
                            {!state.newCustLoading && <CustomerForm
                                {...props}
                                isFormValidated={isNewFormValidated}
                                customerType={state.customerFormData.customers.customerType}
                                customerStatus={state.customerFormData.customers.customerStatus}
                                empList={state.customerFormData.employees.list}
                                customerList={state.customerFormData.customers.list}
                                comp_id={props.comp_id}
                                customerPage={true}
                                user={props.user}
                            />}
                        </DialogContent>
                    </Dialog>
                }
            </div>
    );

    return (
        <div style={{ height: 'calc(100vh - 90px)', width: '100%', overflow: 'hidden' }}>
            {dashBoard}
        </div>
    );
}
